import { defineStore } from 'pinia'
import { invoke } from '~/lib/tauriInvoke'
import { runAction } from '~/stores/storeErrorHandling'
import { useGroupsStore } from '~/stores/groups'
import { useUserStore } from '~/stores/user'
import type { Message } from '~/types/messages'
import {
  emptyNotificationSettings,
  FOREVER_ISO,
  isMuteActive,
  localMuteUntil,
  muteDeadline,
  MuteDuration,
  NotificationLevel,
  NotificationTargetType,
  normalizeNotificationSettings,
  resolveNotification,
  shouldNotify,
  type GroupNotificationSettings,
  type MuteDurationValue,
  type NotificationLevelValue,
  type NotificationTargetTypeValue,
  type ResolvedNotification,
} from '~/lib/notifications/notificationPolicy'

export interface NotificationSettingsPatch {
  level?: NotificationLevelValue
  mute?: MuteDurationValue
  suppress_everyone?: boolean
  suppress_roles?: boolean
}

// setTimeout's delay is a signed 32-bit int; anything longer fires immediately.
const MAX_TIMER_MS = 2 ** 31 - 1

const pending = new Map<string, Promise<void>>()
let expiryTimer: ReturnType<typeof setTimeout> | undefined

function categoryIdOf(groupId: string, channelId: string): string | null {
  const key = channelId.toLowerCase()
  for (const category of useGroupsStore().groupDetails[groupId]?.categories ?? []) {
    if (category.channels.some((c) => c.id.toLowerCase() === key)) return category.id
  }
  return null
}

// Before the settings are loaded, the group list's own `is_muted` still says whether the group is muted.
function fallbackSettings(groupId: string): GroupNotificationSettings {
  const settings = emptyNotificationSettings(groupId, groupDefault(groupId))
  if (useGroupsStore().groups.find((g) => g.id === groupId)?.is_muted) settings.muted_until = FOREVER_ISO
  return settings
}

function groupDefault(groupId: string): number {
  return useGroupsStore().groupDetails[groupId]?.default_notification_level ?? 0
}

// Personal notification settings per group (groups.proto GetNotificationSettings). Cache-first:
// fetched once when a group is opened, then kept fresh by `NotificationSettingsChanged` user events
// (which also sync the other devices) and the results of our own updates.
export const useNotificationSettingsStore = defineStore('notificationSettings', {
  state: () => ({
    byGroup: {} as Record<string, GroupNotificationSettings>,
    // Bumped when a timed mute runs out, so everything resolved against "now" re-evaluates.
    now: Date.now(),
    error: null as string | null,
  }),
  getters: {
    settingsFor: (state) => (groupId: string): GroupNotificationSettings | null => state.byGroup[groupId] ?? null,
    resolveGroup: (state) => (groupId: string): ResolvedNotification =>
      resolveNotification(state.byGroup[groupId] ?? fallbackSettings(groupId), { type: NotificationTargetType.Group, id: groupId }, state.now, groupDefault(groupId)),
    resolveCategory: (state) => (groupId: string, categoryId: string): ResolvedNotification =>
      resolveNotification(state.byGroup[groupId] ?? fallbackSettings(groupId), { type: NotificationTargetType.Category, id: categoryId }, state.now, groupDefault(groupId)),
    resolveChannel: (state) => (groupId: string, channelId: string, categoryId?: string | null): ResolvedNotification =>
      resolveNotification(
        state.byGroup[groupId] ?? fallbackSettings(groupId),
        { type: NotificationTargetType.Channel, id: channelId, categoryId: categoryId ?? categoryIdOf(groupId, channelId) },
        state.now,
        groupDefault(groupId),
      ),
  },
  actions: {
    resetForLogout() {
      pending.clear()
      clearTimeout(expiryTimer)
      expiryTimer = undefined
      this.$reset()
    },

    async ensureLoaded(groupId: string, force = false) {
      if (!groupId || (!force && this.byGroup[groupId])) return
      const inFlight = pending.get(groupId)
      if (inFlight) return inFlight
      const request = (async () => {
        try {
          const raw = await invoke<unknown>('get_group_notification_settings', { groupId })
          const settings = normalizeNotificationSettings(raw)
          if (settings) this.apply(settings)
        } catch (e) {
          // A server without the API yet: the group list's `is_muted` keeps working as before.
          console.warn('[notificationSettings] load failed', groupId, e)
        } finally {
          pending.delete(groupId)
        }
      })()
      pending.set(groupId, request)
      return request
    },

    /** Re-reads every loaded group — after a reconnect, when user events may have been missed. */
    refreshLoaded() {
      for (const groupId of Object.keys(this.byGroup)) void this.ensureLoaded(groupId, true)
    },

    /** Server truth (command result or `NotificationSettingsChanged`); raw payloads are normalized. */
    applyRemote(raw: unknown) {
      const settings = normalizeNotificationSettings(raw)
      if (settings) this.apply(settings)
    },

    apply(settings: GroupNotificationSettings) {
      this.byGroup[settings.group_id] = settings
      this.syncGroupMuteFlag(settings.group_id)
      this.scheduleExpiry()
    },

    // `Group.is_muted` drives the sidebar badge and is what the server computes the same way.
    syncGroupMuteFlag(groupId: string) {
      const settings = this.byGroup[groupId]
      if (!settings) return
      const group = useGroupsStore().groups.find((g) => g.id === groupId)
      if (group) group.is_muted = isMuteActive(settings.muted_until, Date.now())
    },

    // One timer for the nearest timed mute across all groups; when it fires, `now` moves on and
    // whatever was muted until then shows as unmuted again.
    scheduleExpiry() {
      clearTimeout(expiryTimer)
      const now = Date.now()
      let nearest = Infinity
      for (const settings of Object.values(this.byGroup)) {
        for (const until of [settings.muted_until, ...settings.overrides.map((o) => o.muted_until)]) {
          const at = muteDeadline(until)
          if (at !== null && at > now && at < nearest) nearest = at
        }
      }
      if (!Number.isFinite(nearest)) return
      expiryTimer = setTimeout(() => {
        this.now = Date.now()
        for (const groupId of Object.keys(this.byGroup)) this.syncGroupMuteFlag(groupId)
        this.scheduleExpiry()
      }, Math.min(nearest - now + 50, MAX_TIMER_MS))
    },

    /**
     * One change to a group/category/channel row. Applied locally right away (a timed mute gets a
     * local deadline until the server answers with its own), rolled back if the call fails.
     */
    async update(groupId: string, targetType: NotificationTargetTypeValue, targetId: string, patch: NotificationSettingsPatch) {
      const previous = this.byGroup[groupId]
      const base = previous ?? fallbackSettings(groupId)
      const next: GroupNotificationSettings = JSON.parse(JSON.stringify(base))
      const now = Date.now()
      const mutedUntil = patch.mute === undefined || patch.mute === MuteDuration.Unspecified
        ? undefined
        : patch.mute === MuteDuration.Unmute ? null : localMuteUntil(patch.mute, now)

      if (targetType === NotificationTargetType.Group) {
        if (patch.level !== undefined) next.level = patch.level
        if (mutedUntil !== undefined) next.muted_until = mutedUntil
        if (patch.suppress_everyone !== undefined) next.suppress_everyone = patch.suppress_everyone
        if (patch.suppress_roles !== undefined) next.suppress_roles = patch.suppress_roles
      } else {
        const key = targetId.toLowerCase()
        let row = next.overrides.find((o) => o.target_id.toLowerCase() === key)
        if (!row) {
          row = { target_id: targetId, target_type: targetType, level: NotificationLevel.Inherit, muted_until: null }
          next.overrides.push(row)
        }
        if (patch.level !== undefined) row.level = patch.level
        if (mutedUntil !== undefined) row.muted_until = mutedUntil
        // A row with nothing left in it is gone on the server too.
        if (row.level === NotificationLevel.Inherit && !row.muted_until) {
          next.overrides = next.overrides.filter((o) => o !== row)
        }
      }
      this.apply(next)

      return runAction(
        (msg) => {
          this.error = msg
          if (previous) this.apply(previous)
          else {
            delete this.byGroup[groupId]
            const group = useGroupsStore().groups.find((g) => g.id === groupId)
            if (group) group.is_muted = isMuteActive(base.muted_until, Date.now())
          }
        },
        { action: 'updateNotificationSettings', extra: { groupId, targetType, targetId, ...patch } },
        async () => {
          const raw = await invoke<unknown>('update_group_notification_settings', {
            payload: {
              group_id: groupId,
              target_id: targetType === NotificationTargetType.Group ? groupId : targetId,
              target_type: targetType,
              level: patch.level ?? null,
              mute: patch.mute ?? null,
              suppress_everyone: patch.suppress_everyone ?? null,
              suppress_roles: patch.suppress_roles ?? null,
            },
          })
          this.applyRemote(raw)
        },
      )
    },

    mute(groupId: string, targetType: NotificationTargetTypeValue, targetId: string, duration: MuteDurationValue) {
      return this.update(groupId, targetType, targetId, { mute: duration })
    },
    unmute(groupId: string, targetType: NotificationTargetTypeValue, targetId: string) {
      return this.update(groupId, targetType, targetId, { mute: MuteDuration.Unmute })
    },
    setLevel(groupId: string, targetType: NotificationTargetTypeValue, targetId: string, level: NotificationLevelValue) {
      return this.update(groupId, targetType, targetId, { level })
    },
    setSuppress(groupId: string, patch: { suppress_everyone?: boolean, suppress_roles?: boolean }) {
      return this.update(groupId, NotificationTargetType.Group, groupId, patch)
    },

    /** The admin changed the group default: what INHERIT resolves to changes with it. */
    setGroupDefault(groupId: string, level: number) {
      const settings = this.byGroup[groupId]
      if (settings) settings.default_level = level as NotificationLevelValue
    },

    /** Whether a message arriving in this channel should notify me (in-app unread mark + sound). */
    shouldNotifyMessage(groupId: string, message: Message): boolean {
      const groupsStore = useGroupsStore()
      const userId = useUserStore().user?.id ?? ''
      const me = userId.toLowerCase()
      const roleIds = groupsStore.groupUsers[groupId]?.find((u) => u.id.toLowerCase() === me)?.role_ids ?? []
      return shouldNotify(
        this.byGroup[groupId] ?? fallbackSettings(groupId),
        { id: message.channel_id, categoryId: categoryIdOf(groupId, message.channel_id) },
        message,
        { userId, roleIds },
        Date.now(),
        groupDefault(groupId),
      )
    },
  },
})
